import { defineFunctionIfMissing, definePropertySafe } from './backport-utils';

/**
 * When autoNeedsUpdate is true (default), public Texture setters flag the texture for re-upload.
 * @param {THREE.Texture} texture
 */
function flagNeedsUpdate(texture) {
  if (texture.autoNeedsUpdate !== false) {
    texture.needsUpdate = true;
  }
}

/**
 * Public Scene API getters/setters for THREE.Texture.
 */
export const defineTexture = (THREE) => {
  definePropertySafe(THREE?.Texture?.prototype, 'autoNeedsUpdate', {
    get() {
      return this._autoNeedsUpdate !== false;
    },
    set(value) {
      this._autoNeedsUpdate = value;
    },
    enumerable: true,
    configurable: true
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getName', function getName() {
    return this.name;
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setName
   * @param {string} name - Optional name of this texture. Does not flag the texture for
   * re-upload: the name is only used as GPU resource debug metadata at texture creation time.
   */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setName', function setName(name) {
    this.name = name;
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getImage', function getImage() {
    return this.image;
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setImage
   * @param {Object} image - Image data for the texture.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setImage', function setImage(image) {
    this.image = image;
    flagNeedsUpdate(this);
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getWrapS', function getWrapS() {
    return this.wrapS;
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setWrapS
   * @param {number} wrapS - Horizontal (U) wrapping mode.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setWrapS', function setWrapS(wrapS) {
    this.wrapS = wrapS;
    flagNeedsUpdate(this);
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getWrapT', function getWrapT() {
    return this.wrapT;
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setWrapT
   * @param {number} wrapT - Vertical (V) wrapping mode.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setWrapT', function setWrapT(wrapT) {
    this.wrapT = wrapT;
    flagNeedsUpdate(this);
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getMagFilter', function getMagFilter() {
    return this.magFilter;
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setMagFilter
   * @param {number} magFilter - Magnification filter.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setMagFilter', function setMagFilter(magFilter) {
    this.magFilter = magFilter;
    flagNeedsUpdate(this);
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getMinFilter', function getMinFilter() {
    return this.minFilter;
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setMinFilter
   * @param {number} minFilter - Minification filter.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setMinFilter', function setMinFilter(minFilter) {
    this.minFilter = minFilter;
    flagNeedsUpdate(this);
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getOffset', function getOffset() {
    return this.offset.clone();
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setOffset
   * @param {Autodesk.Viewing.Math.Vector2} offset - How many times the texture is offset, in each direction.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setOffset', function setOffset(offset) {
    this.offset.copy(offset);
    flagNeedsUpdate(this);
  });

  defineFunctionIfMissing(THREE?.Texture?.prototype, 'getRepeat', function getRepeat() {
    return this.repeat.clone();
  });

  /**
   * @alias Autodesk.Viewing.Scene.Texture#setRepeat
   * @param {Autodesk.Viewing.Math.Vector2} repeat - How many times the texture is repeated, in each direction.     */
  defineFunctionIfMissing(THREE?.Texture?.prototype, 'setRepeat', function setRepeat(repeat) {
    this.repeat.copy(repeat);
    flagNeedsUpdate(this);
  });
};